import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAspenSocket } from 'hooks/useAspenSocket';
import React from 'react';
import { LightState } from 'types/aspen/state';
import { createCommand } from 'modules/aspenCommands';
import { getColorProperties, getContrastAnalysis } from 'modules/color';
import { useLeafState } from 'context/state/leaf';

// TODO: use the contrast analysis to adjust the switch thumb color based on the background --> switch between dark and light mode

const SwitchTrack = styled(Box, {
  shouldForwardProp: (prop) => !['checked', 'preferLight', 'isGroup'].includes(prop as string),
})<{
  checked: boolean;
  preferLight: boolean;
  isGroup?: boolean | undefined;
}>(({ checked, preferLight, isGroup }) => {
  let background: string;
  let boxShadow: string;

  if (checked) {
    background = preferLight || isGroup ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.35)';
    boxShadow = 'inset 0px 4px 8px rgba(0, 0, 0, 0.3)';
  } else {
    background = 'rgba(192, 192, 192, 0.8)';
    boxShadow = 'inset 0px 4px 6px rgba(0, 0, 0, 0.3)';
  }

  return {
    width: '100%',
    maxWidth: 60,
    height: 34,
    borderRadius: 20,
    background,
    boxShadow,
    filter: preferLight || isGroup ? 'none' : 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.15))',
    cursor: 'pointer',
  };
});

const SwitchThumb = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'checked',
})<{ checked: boolean }>(({ checked }) => ({
  width: '100%',
  maxWidth: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: '#f5f5f5',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.4)',
  position: 'absolute',
  top: 3,
  left: checked ? 29 : 3,
}));

interface LightsPowerProps {
  isGroup?: boolean | undefined;
}

export const LightsPowerSwitch = ({ isGroup }: LightsPowerProps) => {
  const { leaf, dispatch } = useLeafState(); // Universal usage - works for both device and group
  const leafState = leaf.state as LightState;
  const powerState = leafState.power?.power;
  const brightnessLevel = leafState?.brightness?.level || 0;
  const { sendCommand } = useAspenSocket();
  const { colorRgb, brightnessFactor, colorType, temperatureState } = getColorProperties(leafState, brightnessLevel);
  const { preferLight } = getContrastAnalysis(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState);
  const handlePowerSwitch = () => {
    const newPowerState = powerState === 'on' ? 'off' : 'on';
    dispatch({ type: 'SET_POWER', payload: newPowerState, origin: { type: leaf.type, trigger: 'user' } }, () =>
      sendCommand({
        leaf,
        command: createCommand('power', newPowerState),
      }),
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '60px',
        maxHeight: '34px',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <SwitchTrack
        checked={powerState === 'on'}
        preferLight={preferLight}
        isGroup={isGroup}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handlePowerSwitch();
        }}
        role="switch"
        aria-checked={powerState === 'on'}
        aria-label={`${leaf.name || 'Light'} power ${powerState === 'on' ? 'on' : 'off'}`}
        aria-describedby="power-switch-desc"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handlePowerSwitch();
          }
        }}
      >
        <SwitchThumb checked={powerState === 'on'} />
      </SwitchTrack>
    </Box>
  );
};
